import { Observable, Subscriber } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { SupabaseClient, RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { GetResult } from '@supabase/postgrest-js/src/select-query-parser';
import type { PostgrestFilterBuilder } from "@supabase/postgrest-js";

import { Database } from '../models/supabase-types';

type SchemaNameKeys = Exclude<keyof Database, '__InternalSupabase'>;

// Helper type to extract the Row type of a table
type TableRow<
  SchemaName extends SchemaNameKeys,
  TableName extends keyof Database[SchemaName]['Tables']
> = Database[SchemaName]['Tables'][TableName] extends { Row: infer Row } ? Row : never;

// Helper type to extract the Row type of a view
type ViewRow<
  SchemaName extends SchemaNameKeys,
  ViewName extends keyof Database[SchemaName]['Views']
> = Database[SchemaName]['Views'][ViewName] extends { Row: infer Row } ? Row : never;

// Generic QueryBuilder type for a view query
export type QueryBuilderView<
    SchemaName extends SchemaNameKeys,
    ViewName extends keyof SchemaType['Views'],
    SelectColumns extends keyof RowType | '*' = '*',
    SchemaType extends Database[SchemaName] = Database[SchemaName],
    RowType = ViewRow<SchemaName, ViewName>,
    Relationships = SchemaType['Views'][ViewName] extends { Relationships: infer Relation } ? Relation : never,
    ResultType = GetResult<SchemaType, RowType & Record<string, unknown>, ViewName, Relationships, SelectColumns extends string ? SelectColumns : never>
> = PostgrestFilterBuilder<SchemaType, RowType & Record<string, unknown>, ResultType[], ViewName, Relationships>;


/**
 * A generic realtime query class that can watch any table and based on updates fetch data from a view using RxJS.
 * This is more complex than a table query as Supabase can not watch views directly, however we can watch the data source of a view (a table)
 * and then update the queried items using a item id to fetch any changes from the view.
 * @template Database - The root database type containing all schemas.
 */
export class RealtimeQueryView<
    SchemaName extends SchemaNameKeys,
    TableName extends (keyof Database[SchemaName]['Tables']),
    ViewName extends keyof Database[SchemaName]['Views'],
    TableItemType = Database[SchemaName]['Tables'][TableName] extends { Row: infer Row } ? Row : never,
    ViewItemType = Database[SchemaName]['Views'][ViewName] extends { Row: infer Row } ? Row : never
> {
    constructor(
        // Reference to the database client
        private db: SupabaseClient<Database>,
        // Name of the schema of the table and the view to listen to
        private schemaName: SchemaName = 'public' as SchemaName,
        // Name of the table to listen for changes in
        private tableName: TableName,
        private viewName: ViewName,
        // The primary key of the items to listen for (used for indexing (update/insert/delete) the items in the result list with table items or view items)
        private primaryTableKey: (keyof TableItemType) = 'id' as keyof TableItemType,
        private primaryViewKey: (keyof ViewItemType) = 'id' as keyof ViewItemType,
        // Optionally query function (returning fresh instance of a query builder) to get items from an alternative table or view
        private itemQueryFn: () => QueryBuilderView<SchemaName, ViewName> = () => this.db.schema(schemaName).from(viewName as string).select('*'),
        private getForeignViewItemId? : (item: TableItemType) => string
    ) {
    }

    /**
     * Watch changes of a single db item with the given key.
     * @param itemKey 
     * @returns Observer for the item
     */
    public watchDbItem(itemsKey: string, updateFilter?: string): Observable<ViewItemType> {
        // Map to first element of the watched array
        return this.watchDbItemList({ itemsKey, updateFilter }).pipe(map((items) => items[0]));
    }

    /**
     * Watch changes of a list of db items with a single condition
     */
    public watchDbItemList(options?: { itemsKey?: string, updateFilter?: string }): Observable<ViewItemType[]> {
        // Create new instance of query builder
        let queryBuilder = this.itemQueryFn!();
        // Add query filter to the query if specified
        const query = (options?.itemsKey ? queryBuilder.eq(this.primaryTableKey as string, options.itemsKey) : queryBuilder) as PromiseLike<{ data: ViewItemType[] }>;
        // Use default eq filter if a itemsKey is provided, otherwise use default empty filter or custom filter
        const updateFilter = options?.itemsKey && !options?.updateFilter ? `${this.primaryTableKey as string}=eq.${options.itemsKey}` : options?.updateFilter;
        return this.watchDbItemsQuery(query, updateFilter);
    }

    /**
     * Watches for changes in the specified table and returns an updated list of items.
     * @param query The initial query to fetch data.
     * @param updateFilter The filter to apply on realtime updates.
     * @returns An Observable of the updated list of items.
     */
    public watchDbItemsQuery(query: PromiseLike<{ data: ViewItemType[] | null; }>, updateFilter?: string): Observable<ViewItemType[]> {
        let dbChannel: RealtimeChannel | null = null;
        const channelName = `${this.tableName as string}_${updateFilter || 'all'}`;
        return new Observable<ViewItemType[]>((observer) => {
            // Fetch data from the database and the start listening for changes
            query.then((response) => {
                // Send initial items list
                let items = response.data || [];
                observer.next(items);

                // Listen to changes in the table
                dbChannel = this.db.channel(channelName).on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: this.schemaName as string,
                        table: this.tableName as string,
                        ...(updateFilter ? { filter: updateFilter } : {})
                    },
                    async (update: RealtimePostgresChangesPayload<TableItemType & { [key: string]: any; }>) => {
                        // Update list of items
                        await this.updateItemList(update, items, observer);
                    }
                ).subscribe((status) => {
                    // console.log('Channel status:', status, channelName);
                });
            });
        }).pipe(
            finalize(() => {
                // Unsubscribe from the channel when no longer needed
                if (dbChannel) dbChannel.unsubscribe();
                // console.log('Finalizing subscription for', channelName);
            })
        );
    }

    /**
     * Updates the list of items based on the realtime payload.
     * @param update The realtime update payload.
     * @param items The current list of items.
     * @param observer The observer to emit updates.
     * @param itemViewQuery An optional itemView query builder object, which allows to retrieve changed items from a view, instead of the item table itself.
     */
    private async updateItemList(update: RealtimePostgresChangesPayload<TableItemType & { [key: string]: any; }>, items: ViewItemType[], observer: Subscriber<ViewItemType[]>) {
        const matchByPrimaryKey = (item: Partial<TableItemType>, b: ViewItemType) => {
            // Find the existing item in the item list depending if it is a TableItemType or a view ItemType
            return getViewItemId(item) == String(b[this.primaryViewKey]);
        };

        const getViewItemId = (item: Partial<TableItemType>): string => {
            return (typeof this.getForeignViewItemId == 'undefined') ? String(item[this.primaryTableKey]) : this.getForeignViewItemId(item as TableItemType);
        }
        if (update.eventType === 'DELETE') {
            // Remove the item from the list
            const index = items.findIndex((item) => matchByPrimaryKey(update.old, item));
            // TODO: How to not remove the parent process sum if there are other children that depend on it?? Handle this special case outside?  
            if (index >= 0) items.splice(index, 1);
            return observer.next(items);
        }

        let newItem: ViewItemType | TableItemType;
        // Get alternative item (from a view for example) when the source table updates
        if (this.itemQueryFn) {
            // Primary key of view item
            const pkView = this.primaryViewKey as string;
            // Get the item related to the update from related view 
            const viewId = getViewItemId(update.new);
            // Copy the original item query builder
            const itemQuery = this.itemQueryFn();
            const { data, error } = await itemQuery.eq(pkView, viewId).single().then();

            if (error) {
                console.warn(`Could not find new item with primary key '${pkView}'=${viewId} in view based on table (ignore update)`, error);
                return;
            }
            newItem = data as ViewItemType;
        }
        else {
            newItem = update.new as any as ViewItemType;
        }

        // Update the existing item in list
        if (update.eventType == 'UPDATE') {
            const existingIdx = items.findIndex((item) => matchByPrimaryKey(update.new, item));
            if (existingIdx >= 0 && newItem) items[existingIdx] = newItem;
        }

        // Insert the new item in list
        if (update.eventType == 'INSERT' && newItem) items.push(newItem);

        return observer.next(items);
    }
}
export function createViewQuery<
  SchemaName extends SchemaNameKeys,
  TableName extends keyof Database[SchemaName]['Tables'],
  ViewName extends keyof Database[SchemaName]['Views']
>(
  db: SupabaseClient<Database>,
  schemaName: SchemaName,
  tableName: TableName,
  viewName: ViewName,
  primaryTableKey: keyof TableRow<SchemaName, TableName>,
  primaryViewKey: keyof ViewRow<SchemaName, ViewName>,
  itemQueryFn: () => QueryBuilderView<SchemaName, ViewName> = () => db.schema(schemaName).from(viewName as string).select('*'),
  getForeignViewItemId?: (item: TableRow<SchemaName, TableName>) => string
) {
  return new RealtimeQueryView<SchemaName, TableName, ViewName>(
    db,
    schemaName,
    tableName,
    viewName,
    primaryTableKey,
    primaryViewKey,
    itemQueryFn,
    getForeignViewItemId
  );
}
